import { takeLatest, call, put } from 'redux-saga/effects';
import { EventLogsApi, GatewayManagementApi } from 'api';
import { apiCallStatus, NOTIFICATION_TYPES } from 'helpers/constants';
import logger from 'helpers/logger';
import * as constants from './constants';
import { translate } from 'helpers/translateHelper';
import { notify } from 'sharedComponents/GeneralNotification';
import { showDialogToast, DialogToastVariant } from 'sharedComponents/DialogToast';
import { getGatewayEventsLogAction, getGatewayEventsAttachmentAction, getCheckDownloadLogsLimitAction, getDownloadLogsZipAction } from './actions';
import { b64toBlob, downloadFile } from 'helpers/download-file-util';
import { DownloadLogsToastProgressDurationMs } from './DownloadLogsModal/constants';

export function* fetchGatewayEventsLog(action) {
    const isAppending = action.type === constants.LOAD_MORE_GATEWAY_EVENTS;

    // Only set main loading status for initial load, not for load more
    if (!isAppending) {
        yield put(getGatewayEventsLogAction(apiCallStatus.LOADING, null, null, isAppending));
    }

    try {
        const {
            dcuSerialNumber,
            organizationId,
            userEmailAddress,
            messageTextContains,
            eventSource,
            timestampFrom,
            timestampTo,
            cursor,
            pageSize,
            severityCodes,
            reportType,
            fileNameStartsWith
        } = action.payload;

        const response = yield call(
            GatewayManagementApi.gatewayEvents,
            {
                dcuSerialNumber,
                organizationId,
                userEmailAddress,
                ...messageTextContains !== undefined && { messageTextContains },
                eventSource,
                timestampFrom,
                timestampTo,
                cursor,
                pageSize,
                severityCodes,
                ...reportType !== undefined && { reportType },
                ...fileNameStartsWith !== undefined && { fileNameStartsWith }
            }
        );

        yield put(getGatewayEventsLogAction(apiCallStatus.SUCCESS, response, null, isAppending));
        logger.debug('Gateway Events successfully loaded', {
            eventSource,
            count: response?.events?.length,
            hasMore: !!response?.nextCursor,
            isAppending
        });
    } catch (err) {
        yield put(getGatewayEventsLogAction(apiCallStatus.ERROR, [], err, isAppending));
        notify('error', translate('ABB.Powertrain.Frontend.errorLoadingGatewayEventsMessage'));
        logger.error('Error loading gateway events', err);
    }
}

function* downloadGatewayEventAttachment(action) {
    yield put(getGatewayEventsAttachmentAction(apiCallStatus.LOADING));

    try {
        const { globalEventId, eventSource, fileName } = action.payload;

        // Get fileId from gateway events attachments API
        const [attachmentResponse] = yield call(
            GatewayManagementApi.gatewayEventsAttachments,
            globalEventId,
            eventSource
        );

        if (!attachmentResponse?.fileId) {
            throw new Error('No fileId received from server');
        }

        // Download file using the fileId
        const downloadResponse = yield call(EventLogsApi.downloadFile, attachmentResponse.fileId);

        if (!downloadResponse?.fileContent) {
            throw new Error('No file content received from server');
        }

        // Trigger browser download
        const blob = b64toBlob(downloadResponse.fileContent);
        const downloadFileName = fileName || downloadResponse.fileName || `event_attachment_${globalEventId}.zip`;
        downloadFile(downloadFileName, blob);

        // Update state with success
        yield put(getGatewayEventsAttachmentAction(apiCallStatus.SUCCESS, {
            fileId: attachmentResponse.fileId,
            fileContent: downloadResponse.fileContent,
            fileName: downloadFileName
        }));

        logger.debug('Gateway Event Attachment successfully downloaded', { globalEventId, fileName: downloadFileName });
    } catch (err) {
        yield put(getGatewayEventsAttachmentAction(apiCallStatus.ERROR, null, err));
        notify('error', translate('ABB.Powertrain.Frontend.errorDownloadingGatewayEventAttachmentMessage'));
        logger.error('Error downloading gateway event attachment', err);
    }
}

function* checkDownloadLogsLimit(action) {
    yield put(getCheckDownloadLogsLimitAction(apiCallStatus.LOADING));

    try {
        const response = yield call(GatewayManagementApi.downloadLogLimitCheck, action.payload);
        yield put(getCheckDownloadLogsLimitAction(apiCallStatus.SUCCESS, response));
        logger.debug('Download logs limit checked successfully', response);
    } catch (err) {
        yield put(getCheckDownloadLogsLimitAction(apiCallStatus.ERROR, null, err));
        notify(err?.message, NOTIFICATION_TYPES.ERROR, true);
        logger.error('Error checking download logs limit', err);
    }
}

function* downloadLogsZip(action) {
    yield put(getDownloadLogsZipAction(apiCallStatus.LOADING));
    try {
        const blob = yield call(GatewayManagementApi.downloadLogs, action.payload);
        downloadFile(action.fileName, blob);
        yield put(getDownloadLogsZipAction(apiCallStatus.SUCCESS));
        showDialogToast(
            translate('ABB.Powertrain.Frontend.downloadLogsStartedToastMessage'),
            DialogToastVariant.INFO,
            DownloadLogsToastProgressDurationMs,
            true,
            true
        );
        logger.debug('Download logs zip file downloaded successfully', { fileName: action.fileName });
    } catch (err) {
        yield put(getDownloadLogsZipAction(apiCallStatus.ERROR, null, err));
        notify(err?.message, NOTIFICATION_TYPES.ERROR, true);
        logger.error('Error downloading logs zip file', err);
    }
}

export function* gatewayEventsSagaWatchers() {
    yield takeLatest(constants.FETCH_GATEWAY_EVENTS_LOG, fetchGatewayEventsLog);
    yield takeLatest(constants.LOAD_MORE_GATEWAY_EVENTS, fetchGatewayEventsLog);
    yield takeLatest(constants.FETCH_GATEWAY_EVENTS_ATTACHMENT, downloadGatewayEventAttachment);
    yield takeLatest(constants.CHECK_DOWNLOAD_LOGS_LIMIT, checkDownloadLogsLimit);
    yield takeLatest(constants.DOWNLOAD_LOGS_ZIP, downloadLogsZip);
}
